import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { CredentialType } from 'enums/CredentialType';
import { MessageType } from 'enums/MessageType';
import { environment } from 'environments/environment';
import { AdUserModel } from 'models/generated/Identity.Api.Models.AdUser';
import { CredentialsDtoModel } from 'models/generated/Identity.Api.Models.CredentialsDto';
import { TeamModel } from 'models/generated/Identity.Api.Models.Team';
import { UserSettingsModel, UserModel } from 'models/generated/Identity.Api.Models.User';
import { UserQualificationCallViewModel } from 'models/generated/Models.UserQualificationCallView';
import { SelectListItemModel } from 'models/SelectList';
import { TranslatePipe } from 'pipes/translate.pipe';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { PushService } from 'services/push.service';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { RelationService } from './relation.service';


@Injectable()
export class UserService  {

	public ready = false;
	public onError = new EventEmitter<HttpErrorResponse>();

	private baseUrl = `${environment.gatewayUrl}/api/users`;
	private adUrl = `${environment.gatewayUrl}/api/active-directory`;

	private _users?: UserModel[];
	private _teams?: TeamModel[];

	private currentUser!: UserModel;
	private currentUserCredentials?: CredentialType[];
	private currentUserCustomCredentials?: string[];
	private credentialsSubject = new Subject<void>();

	constructor(private readonly http: HttpClient, private readonly pushService: PushService, private readonly translate: TranslatePipe, private readonly customDatePipe: CustomDatePipe) {
		this.pushService.onChanges(MessageType.UserChanges).subscribe(() => this.reloadChanges());
		this.pushService.onChanges(MessageType.TeamChanges).subscribe(() => this.reloadChanges());
		this.pushService.onChanges<{ userId: string }>(MessageType.DashboardChanges).subscribe((message) => message.data?.userId == this.currentUser.id ? this.loadCurrentUser() : null);
	}

	private async reloadChanges() {
		await Promise.all([
			this.loadCurrentUser(),
			this.getAllUsers(),
			this.getAllTeams()
		]);
		const userInfo = RelationService.decycle({ users: this._users, teams: this._teams, currentUser: this.currentUser });
		localStorage.setItem('gorm.user-info', JSON.stringify(userInfo));
	}

	async resolve() {
		const cachedUserInfo: { users: UserModel[], teams: TeamModel[], currentUser: UserModel; } | null = JSON.parse(localStorage.getItem('gorm.user-info') ?? 'null');
		if (cachedUserInfo) {
			RelationService.retrocycle(cachedUserInfo);
			this._users = cachedUserInfo.users;
			this._teams = cachedUserInfo.teams;
			this.setCurrentUser(cachedUserInfo.currentUser);
			this.ready = true;							// Set service as ready, as cached data should still be useful
			(new Promise(f => setTimeout(f, 3000)))		// Delay reload to prevent blocking other requests which might block the GORM UI load
				.then(_ => this.reloadChanges());		//reload to overwrite cached data with fresh data
			return;
		}
		try {
			await this.reloadChanges();
			this.ready = true;
		}
		catch (error) {
			if (error instanceof HttpErrorResponse) this.onError.next(error);
		}
	}

	private async loadCurrentUser() {
		const user = await firstValueFrom(this.http.get<UserModel>(`${this.baseUrl}/current`));
		if (!user) throw Error('Not logged in');
		this.setCurrentUser(user);
	}

	private setCurrentUser(user: UserModel) {
		const credentials: CredentialType[] = [];
		const customCredentials: string[] = [];
		user.userTeams.forEach(userTeam => {
			if (userTeam.team) {
				userTeam.team.teamCredentials.forEach(teamCredential => credentials.push(teamCredential.credential));
				userTeam.team.teamCustomCredentials.forEach(tcc => {
					if (!customCredentials.find(c => c == tcc.customCredential))
						customCredentials.push(tcc.customCredential);
				});
			}
		});

		if (user.admin && credentials.find(c => c == CredentialType.TeamsAccessFull) == undefined) {
			credentials.push(CredentialType.TeamsAccessFull);
		}

		let changedCredentials = false;
		if (this.currentUserCredentials) {
			if (credentials.findIndex(c => this.currentUserCredentials!.indexOf(c) < 0) >= 0) changedCredentials = true;
			if (this.currentUserCredentials.findIndex(c => credentials.indexOf(c) < 0) >= 0) changedCredentials = true;
		}
		if (this.currentUserCustomCredentials) {
			if (customCredentials.findIndex(c => this.currentUserCustomCredentials!.indexOf(c) < 0) >= 0) changedCredentials = true;
			if (this.currentUserCustomCredentials.findIndex(c => customCredentials.indexOf(c) < 0) >= 0) changedCredentials = true;
		}

		this.currentUser = user;
		this.pushService.currentUserId = user.id;
		this.currentUserCredentials = credentials;
		this.currentUserCustomCredentials = customCredentials;

		if (changedCredentials) this.credentialsSubject.next();
	}

	get users(): UserModel[] {
		if (!this._users) throw new Error('User list not yet loaded from backend');
		return this._users;
	}

	user(id?: string): UserModel | null {
		return this.users.find(user => user.id === id) ?? null;
	}

	get teams(): TeamModel[] {
		if (!this._teams) throw new Error('Team list not yet loaded from backend');
		return this._teams;
	}

	team(id?: string): TeamModel | null {
		return this.teams.find(team => team.id === id) ?? null;
	}

	/**
	 * Retrives all users from the backend, and stores them in a local variable.
	 * Use property 'users' to access stored data.
	 */
	getAllUsers(): Promise<UserModel[]> {
		return firstValueFrom(this.http.get<UserModel[]>(this.baseUrl)).then((users: UserModel[]) => {
			this._users = users;
			return users;
		});
	}

	getAllActiveUsers(): Promise<UserModel[]> {
		return this.getAllUsers().then(users => users.filter(u => !u.deleted));
	}

	get activeUsers(): UserModel[] {
		return this.users.filter(u => !u.deleted);
	}

	getAllAdUsers(): Promise<AdUserModel[]> {
		return firstValueFrom(this.http.get<AdUserModel[]>(`${this.adUrl}/users`));
	}

	adUserIsPerson(user: AdUserModel) {
		if (!user.mail || !user.givenName) return false;
		const acceptedMailDomains = ['grouponline.dk', 'web-koncept.dk'];
		if (user.mail.split('@')[0].length == 3 && acceptedMailDomains.includes(user.mail.split('@')[1])) return true;
		return false;
	}

	/**
	 * Retrives user from the backend.
	 * Use method 'user' to access stored data.
	 */
	getUserById(id?: string): Promise<UserModel> {
		return firstValueFrom(this.http.get<UserModel>(`${this.baseUrl}/${id}`));
	}

	getCurrentUser(): UserModel {
		return this.currentUser;
	}

	getCredentials(): CredentialType[] {
		if (!this.currentUserCredentials) throw new Error('Tried reading user credentials before user was loaded or read from cache');
		return this.currentUserCredentials;
	}

	getCustomCredentials(): string[] {
		if (!this.currentUserCustomCredentials) throw new Error('Tried reading user credentials before user was loaded or read from cache');
		return this.currentUserCustomCredentials;
	}

	updateUser(user: UserModel): Promise<UserModel> {
		if (!user.id) return firstValueFrom(this.http.post<UserModel>(this.baseUrl, user));
		return firstValueFrom(this.http.put<UserModel>(`${this.baseUrl}/${user.id}`, user));
	}

	/**
	 * Retrives all teams from the backend, and stores them in a local variable.
	 * Use property 'teams' to access stored data.
	 */
	getAllTeams(): Promise<TeamModel[]> {
		return firstValueFrom(this.http.get<TeamModel[]>(`${this.baseUrl}/teams`)).then((teams: TeamModel[]) => {
			this._teams = teams;
			return teams;
		});
	}

	getAllActiveTeams(): Promise<TeamModel[]> {
		return this.getAllTeams().then(teams => teams.filter(t => !t.deleted));
	}

	get activeTeams(): TeamModel[] {
		return this.teams.filter(t => !t.deleted);
	}

	/**
	 * Retrives team from the backend.
	 * Use method 'team' to access stored data.
	 */
	getTeamById(id: string): Promise<TeamModel> {
		return firstValueFrom(this.http.get<TeamModel>(`${this.baseUrl}/teams/${id}`));
	}

	createTeam(team: TeamModel): Promise<TeamModel> {
		return firstValueFrom(this.http.post<TeamModel>(`${this.baseUrl}/teams`, team));
	}

	updateTeam(team: TeamModel): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/teams/${team.id}`, team));
	}

	deleteTeam(team: TeamModel): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/teams/${team.id}`));
	}

	getUserInitials(user: { username: string }): string {
		return this.getUserInitialsByUsername(user.username);
	}

	getUserInitialsByUserId(userId?: string): string {
		const user = this.user(userId);
		if (!user) return '';
		return this.getUserInitials(user);
	}

	getUserInitialsByUsername(username: string): string {
		return username.substr(username.length - 3).toUpperCase();
	}

	getUsersWithQualificationCallData(): Promise<UserQualificationCallViewModel[]> {
		return firstValueFrom(this.http.get<UserQualificationCallViewModel[]>(`${this.baseUrl}/get-all-users-with-qualification-call-data`));
	}

	getUsernameWithoutDomain(user: UserModel): string {
		return this.getUsernameWithoutDomainByUsername(user.username);
	}

	getUsernameWithoutDomainByUsername(username: string): string {
		return username.indexOf('\\') != -1 ?
			username.substr(username.lastIndexOf('\\') + 1) :
			username;
	}

	private userImageUrls = new Map<string, Promise<string | undefined>>();
	async getProfileImageUrlByUserId(id?: string) {
		if (!id) return undefined;
		if (!this.userImageUrls.has(id)) this.userImageUrls.set(id, this.downloadProfileImage(id));
		return await this.userImageUrls.get(id);
	}

	private async downloadProfileImage(id: string): Promise<string | undefined> {
		try {
			const image = await firstValueFrom(this.http.get(`${this.baseUrl}/${id}/image`, { responseType: 'blob' }));
			if (!image) return undefined;
			// Only one object URL gets created per user ID due to caching in getProfileImageUrlByUserId()
			// We don't need to call revokeObjectUrl(), as those objects get cleared from memory as soon as the GORM tab is closed
			return window.URL.createObjectURL(image);
		}
		catch (error: unknown) {
			if (error instanceof HttpErrorResponse && error.status === 404) return undefined;
			throw error;
		}
	}

	updateTeamCredentials(teamId: string, enabledCredentials: CredentialType[], customCredentials: string[]): Promise<void> {
		let credentialDto = new CredentialsDtoModel();
		credentialDto.credentials = enabledCredentials;
		credentialDto.customCredentials = customCredentials;
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/teams/${teamId}/credentials`, credentialDto));
	}

	deleteUser(user: UserModel): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${user.id}`));
	}

	getUserSelectList(includeDeleted: boolean = false, includeSystemUser: boolean = false) {

		const list = [...(includeDeleted ? this.users : this.activeUsers)] // Clone list to avoid changing the original order in the service
			.sort((a, b) => {
				if (!!a.deleted != !!b.deleted) return a.deleted ? 1 : -1;
				return a.name.localeCompare(b.name);
			})
			.map(user => new SelectListItemModel(user.id!, !user.deleted ? user.name : this.translate.transform('general.deleted-user', { name: user.name, deleted: this.customDatePipe.transform(user.deleted, 'shortDate') })));
		if (includeSystemUser) list.unshift(new SelectListItemModel('00000000-0000-0000-0000-000000000000', this.translate.transform('general.system-user')));
		return list;
	}

	updateSettings(userId: string, settings: UserSettingsModel) {
		settings.include(s => s.dashboardElementSettings);
		return firstValueFrom(this.http.put<void>(`${this.baseUrl}/${userId}/settings`, settings));
	}

	onCredentialsChanged(): Observable<void> {
		return this.credentialsSubject;
	}
}
